import { LoadingOutlined } from '@ant-design/icons';
import { CallSendMobileMessageResponse, SMSPostRequest } from '@gooduncles/gu-app-schema';
import { Button, Modal, Popover, message, notification } from 'antd';
import { HttpsCallableResult } from 'firebase/functions';
import {
  FC,
  ForwardRefRenderFunction,
  createRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { errorObjectToString } from 'src/utils/common';
import { callSendMobileMessage } from 'src/utils/firebase/firebase-callable';
import { addMobileMessageSendRequestForPurchaseOrder } from 'src/utils/firebase/firebase-short-cut';
import { AuditLogger } from 'src/utils/audit-logger';

import classes from './SendPurchaseOrderSmsModal.module.scss';

const auditLogger = AuditLogger.getInstance();

interface RequestItemProps {
  request: SMSPostRequest & { purchaseOrderId: string };
}

interface RequestItemHandle {
  result: HttpsCallableResult<CallSendMobileMessageResponse> | null | undefined;
  send: () => Promise<HttpsCallableResult<CallSendMobileMessageResponse> | null>;
}

const RequestItem: ForwardRefRenderFunction<RequestItemHandle, RequestItemProps> = ({ request }, forwardRef) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<HttpsCallableResult<CallSendMobileMessageResponse> | null>();

  useImperativeHandle(forwardRef, () => ({
    send: async () => {
      setLoading(true);
      try {
        const { purchaseOrderId, ...rest } = request;
        const result = await callSendMobileMessage(rest);
        // 문자 발송 결과가 성공한 경우에만 발송 요청을 추가하며, 요청만 전달하고, 비동기로 처리한다.
        if (result.data.mobileMessageDocId) {
          addMobileMessageSendRequestForPurchaseOrder(purchaseOrderId, result.data.mobileMessageDocId);
        }
        if (result.data.result === 'error') {
          throw new Error(
            `${request.recipientList[0].recipientGroupingKey} 전송실패.\n${result.data.reason ?? '알 수 없는 에러'}`,
          );
        }
        setResult(result);
        return result;
      } catch (err) {
        const errorMsg = errorObjectToString(err);
        message.error(errorMsg);
        setResult(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    result,
  }));

  return (
    <div>
      {request.recipientList.map((recipient, idx) => {
        return (
          <div key={`recipient-${idx}`} className={classes.recipient}>
            <p>{recipient.recipientNo}</p>
            <p>{recipient.recipientGroupingKey}</p>
            <p>
              {loading ? (
                <LoadingOutlined style={{ color: 'var(--blue400)' }} />
              ) : result ? (
                result.data.result === 'success' ? (
                  '🟢'
                ) : (
                  '❌'
                )
              ) : (
                '-'
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const RequestItemWithRef = forwardRef(RequestItem);

interface SendPurchaseOrderSmsModalProps {
  requests: (SMSPostRequest & { purchaseOrderId: string })[];
  failedOrderIds: string[];
}

const SendPurchaseOrderSmsModal: FC<SendPurchaseOrderSmsModalProps> = ({ requests, failedOrderIds }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // send를 진행하면 requests를 생성하는 원본 데이터가 변경되어 request에 대한 갱신이 일어난다. ref가 유지되도록 useMemo를 사용한다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestRefs = useMemo(() => requests.map(() => createRef<RequestItemHandle>()), [requests.length]);
  const requestCount = requests.flatMap((request) => request.recipientList).length;
  const waitingCount = requestRefs.filter((request) => request.current?.result === undefined).length;

  const showModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOk = useCallback(async () => {
    setLoading(true);
    const promises = requestRefs.map(async (ref) => {
      try {
        if (!ref.current || !ref.current.send) {
          throw new Error('ref가 손상되었습니다.');
        }
        await ref.current?.send();
      } catch (error) {
        console.error(error);
        const description = errorObjectToString(error);
        notification.error({
          message: '발송에 실패했습니다.',
          description,
        });
        auditLogger.log({
          action: 'SEND',
          entity: 'purchaseOrder',
          entityId: `sms-batch-error`,
          summary: `[발주서 문자 일괄 발송] ${description}`,
          level: 'error',
        });
      }
    });
    await Promise.all(promises);
    const successCount = requestRefs.filter((ref) => ref.current?.result?.data?.result === 'success').length;
    auditLogger.log({
      action: 'SEND',
      entity: 'purchaseOrder',
      entityId: `sms-batch-${requests.length}`,
      summary: `발주서 SMS 일괄 발송 (${successCount}/${requests.length}건 성공)`,
      metadata: { totalCount: requests.length, successCount },
    });
    setLoading(false);
  }, [requestRefs, requests.length]);

  const SendButton = (
    <Button type='primary' onClick={showModal} disabled={requests.length === 0}>
      문자 일괄 발송({requestCount}건)
    </Button>
  );

  return (
    <>
      {failedOrderIds.length > 0 ? (
        <Popover content={`발송 불가 ${failedOrderIds.length}건이 있습니다.`} open>
          {SendButton}
        </Popover>
      ) : (
        SendButton
      )}
      <Modal
        title={`문자 일괄 발송 - ${requestCount}건`}
        open={open}
        okText='일괄 발송'
        onOk={handleOk}
        onCancel={handleCancel}
        cancelText='닫기'
        okButtonProps={{ loading, disabled: requests.length === 0 || waitingCount === 0 }}
        cancelButtonProps={{ loading }}
        maskClosable={false}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
          },
        }}
        destroyOnClose
      >
        <div className={classes.recipientHeader}>
          <p>수신번호</p>
          <p>수신자</p>
          <p>결과</p>
        </div>
        {requests.map((request, idx) => (
          <RequestItemWithRef key={`request-${idx}`} ref={requestRefs[idx]} request={request} />
        ))}
      </Modal>
    </>
  );
};

export default SendPurchaseOrderSmsModal;
